import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FaArrowLeft, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { normalizeIngredient, getSupportedUnits, denormalizeIngredient } from '../utils/unitConverter';
import MachineValidationAlert from '../components/MachineValidationAlert';
import PersonalRecipeAnalysis from '../components/PersonalRecipeAnalysis';
import AliasesEditor from '../components/AliasesEditor';

// Recipe-type → recommended default target_brix (°Bx). Mirrors the values
// in AddRecipePage.js and matches the midpoint of each type's BAND in
// server.py::admin_brix_outliers. Kept in sync manually — see PRD notes.
// Feb 2026: widened to reflect real recipes (see PRD #52).
const TYPE_DEFAULT_BRIX = {
  klassisk: 15,
  juice: 13,
  sodavand: 20,
  smoothie: 17,
  maelk: 16,
  kaffe: 14,
  cocktail: 20,
  sukkerfri: 3,
  sport: 8,
};

// Recommended Brix band per type — must equal server-side BANDS.
// Vejledende (soft) hints — hard severity handled by machine validator.
const TYPE_BRIX_BAND = {
  klassisk: [10, 20],
  juice: [8, 18],
  sodavand: [15, 25],
  smoothie: [12, 22],
  maelk: [12, 20],
  kaffe: [10, 18],
  cocktail: [15, 25],
  sukkerfri: [0, 6],
  sport: [4, 12],
};

const EditRecipePage = ({ sessionId }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageRightsConfirmed, setImageRightsConfirmed] = useState(false);
  const [showVolumeInfo, setShowVolumeInfo] = useState(false);
  const [showBrixInfo, setShowBrixInfo] = useState(false);
  const [supportedUnits, setSupportedUnits] = useState(['ml', 'dl', 'l']);

  // Tracks whether the user has TYPED in the Sukkergrad field on this
  // edit session. Purpose: keep auto-syncing target_brix to the type's
  // default on every type-change *until* the user manually overrides the
  // value. Without this, the previous logic locked after the first
  // auto-apply (because the value was no longer 0), so switching kaffe →
  // cocktail would stay at 12 instead of moving to 16.
  //
  // Seeding rule (below in fetchRecipe): treated as "manual" iff the
  // loaded recipe already carries a non-zero target_brix — the assumption
  // being that if a value survived from a previous save, it's the user's
  // deliberate choice.
  const [brixManuallyEdited, setBrixManuallyEdited] = useState(false);
  // Live freeze-point calculation via /api/recipe-analysis/freeze-point.
  // Én kilde til sandhed — samme motor bruges af PersonalRecipeAnalysis
  // og MachineValidationAlert. Debounced 400ms mod tastetryk.
  const [freezeCalc, setFreezeCalc] = useState(null);
  // Eksplicit override af "auto/manual"-tilstand for freeze-feltet.
  // `null` = brug tolerance-heuristik ved genindlæsning. Sættes til
  // `true` når brugeren fjerner fluebenet, `false` når han sætter det på.
  const [freezeManualOverride, setFreezeManualOverride] = useState(null);
  useEffect(() => {
    if (!recipe?.target_brix) return;
    const timer = setTimeout(async () => {
      try {
        const response = await axios.post(`${API}/recipe-analysis/freeze-point`, {
          recipe: {
            target_brix: parseFloat(recipe.target_brix) || 0,
            alcohol_flag: !!recipe.alcohol_flag,
            alcohol_percentage: recipe.alcohol_percentage,
            recommended_freeze_celsius: recipe.recommended_freeze_celsius,
          },
        });
        setFreezeCalc(response.data);
      } catch (err) {
        console.warn('[FreezePoint] calc failed', err);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [recipe?.target_brix, recipe?.alcohol_flag, recipe?.alcohol_percentage, recipe?.recommended_freeze_celsius]);

  useEffect(() => {
    fetchRecipe();
    fetchSupportedUnits();
  }, [id, sessionId]);

  const fetchSupportedUnits = async () => {
    try {
      const lang = i18n.language || 'da';
      const response = await axios.get(`${API}/units/supported?language=${lang}`);
      setSupportedUnits(response.data.units);
    } catch (error) {
      console.error('Could not fetch supported units:', error);
      setSupportedUnits(['ml', 'dl', 'l']);
    }
  };

  const fetchRecipe = async () => {
    try {
      const response = await axios.get(`${API}/recipes/${id}?session_id=${sessionId}&lang=da`);
      
      // Denormalize ingredients for display (convert from ml back to display unit)
      const denormalizedRecipe = {
        ...response.data,
        ingredients: response.data.ingredients.map(ing => {
          // If ingredient has quantity_ml, use denormalize
          if (ing.quantity_ml) {
            return denormalizeIngredient(ing);
          }
          // Otherwise keep as-is (old format)
          return ing;
        })
      };
      
      setRecipe(denormalizedRecipe);
      setImagePreview(denormalizedRecipe.image_url);
      // Seed the "manual" flag from the loaded recipe: non-zero brix is
      // treated as the user's saved choice and locked against auto-sync;
      // zero/empty means "help me fill this in".
      const loadedBrixNum = parseFloat(denormalizedRecipe.target_brix);
      const loadedIsZeroOrEmpty =
        denormalizedRecipe.target_brix === '' ||
        denormalizedRecipe.target_brix === null ||
        denormalizedRecipe.target_brix === undefined ||
        isNaN(loadedBrixNum) ||
        loadedBrixNum === 0;
      setBrixManuallyEdited(!loadedIsZeroOrEmpty);
    } catch (error) {
      console.error('Error fetching recipe:', error);
      toast.error(t('addRecipe.fetchError', 'Kunne ikke hente opskrift'));
      navigate('/recipes');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        toast.error(t('addRecipe.imageSizeError', 'Billede må maks være 5MB'));
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return recipe.image_url;
    
    const formData = new FormData();
    formData.append('file', imageFile);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.image_url;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Kunne ikke uploade billede');
      return recipe.image_url;
    }
  };

  const addIngredient = () => {
    setRecipe({
      ...recipe,
      ingredients: [
        ...recipe.ingredients,
        { name: '', category_key: '', quantity: '', unit: 'ml', role: 'required', brix: null }
      ]
    });
    setTimeout(() => {
      const ingredientsSection = document.querySelector('.ingredients-section');
      if (ingredientsSection) {
        const lastIngredient = ingredientsSection.querySelector('.ingredient-item:last-child');
        if (lastIngredient) {
          lastIngredient.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }, 100);
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...recipe.ingredients];
    newIngredients[index][field] = value;
    setRecipe({ ...recipe, ingredients: newIngredients });
  };

  const removeIngredient = (index) => {
    if (recipe.ingredients.length === 1) {
      toast.error('Du skal have mindst 1 ingrediens');
      return;
    }
    setRecipe({
      ...recipe,
      ingredients: recipe.ingredients.filter((_, i) => i !== index)
    });
  };

  const moveIngredientUp = (index) => {
    if (index === 0) return;
    const newIngredients = [...recipe.ingredients];
    [newIngredients[index - 1], newIngredients[index]] = [newIngredients[index], newIngredients[index - 1]];
    setRecipe({ ...recipe, ingredients: newIngredients });
  };

  const moveIngredientDown = (index) => {
    if (index === recipe.ingredients.length - 1) return;
    const newIngredients = [...recipe.ingredients];
    [newIngredients[index], newIngredients[index + 1]] = [newIngredients[index + 1], newIngredients[index]];
    setRecipe({ ...recipe, ingredients: newIngredients });
  };

  const addStep = () => {
    setRecipe({ ...recipe, steps: [...recipe.steps, ''] });
    setTimeout(() => {
      const stepsSection = document.querySelector('.steps-section');
      if (stepsSection) {
        const lastStep = stepsSection.querySelector('textarea:last-of-type');
        if (lastStep) {
          lastStep.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          lastStep.focus();
        }
      }
    }, 100);
  };

  const updateStep = (index, value) => {
    const newSteps = [...recipe.steps];
    newSteps[index] = value;
    setRecipe({ ...recipe, steps: newSteps });
  };

  const removeStep = (index) => {
    if (recipe.steps.length === 1) {
      toast.error('Du skal have mindst 1 trin');
      return;
    }
    setRecipe({ ...recipe, steps: recipe.steps.filter((_, i) => i !== index) });
  };

  const moveStepUp = (index) => {
    if (index === 0) return;
    const newSteps = [...recipe.steps];
    [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
    setRecipe({ ...recipe, steps: newSteps });
  };

  const moveStepDown = (index) => {
    if (index === recipe.steps.length - 1) return;
    const newSteps = [...recipe.steps];
    [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
    setRecipe({ ...recipe, steps: newSteps });
  };

  const addTag = () => {
    if (tagInput.trim() && !recipe.tags.includes(tagInput.trim())) {
      setRecipe({ ...recipe, tags: [...recipe.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setRecipe({ ...recipe, tags: recipe.tags.filter(t => t !== tag) });
  };

  const submitRecipe = async (e) => {
    e.preventDefault();
    
    if (!recipe.name || !recipe.description) {
      toast.error('Udfyld navn og beskrivelse');
      return;
    }

    if (!recipe.base_volume_ml || recipe.base_volume_ml <= 0) {
      toast.error('Basis volumen er obligatorisk og skal være større end 0');
      return;
    }

    if (recipe.ingredients.some(ing => !ing.name || !ing.category_key)) {
      toast.error('Alle ingredienser skal have navn og kategori');
      return;
    }

    if (recipe.steps.some(step => !step.trim())) {
      toast.error('Alle trin skal udfyldes');
      return;
    }

    setSaving(true);
    try {
      const imageUrl = await uploadImage();
      
      // Normalize ingredients to ml before sending to backend
      const normalizedIngredients = recipe.ingredients.map(ing => {
        const normalized = normalizeIngredient({
          ...ing,
          quantity: parseFloat(ing.quantity),
          brix: ing.brix ? parseFloat(ing.brix) : null
        });
        return normalized;
      });

      // Anbefalet maskintemperatur: hvis "Beregn automatisk" er aktiv, hent
      // en frisk beregnet værdi fra backend LIGE FØR save. Fjerner alle
      // race-conditions med den debouncede live-freezeCalc-state og sikrer at
      // recommended_freeze_celsius altid er et tal i DB — så den vises på
      // opskriftskortet, detaljesiden og i editoren.
      const rfcRaw = recipe.recommended_freeze_celsius;
      const rfcIsEmpty = rfcRaw === '' || rfcRaw === null || rfcRaw === undefined;
      // Ved genindlæsning bruger vi samme tolerance-heuristik som render-koden.
      const computedAtRender = freezeCalc?.computed_machine_temp_c;
      const heuristicIsManual = !rfcIsEmpty && (
        computedAtRender == null || Math.abs(parseFloat(rfcRaw) - computedAtRender) > 0.1
      );
      const isManualAtSave = freezeManualOverride ?? heuristicIsManual;
      let rfcToStore;
      if (isManualAtSave) {
        rfcToStore = rfcIsEmpty ? null : parseFloat(rfcRaw);
      } else {
        // Auto mode — sync-kald til backend med de AKTUELLE Brix/alkohol-værdier.
        try {
          const freshResp = await axios.post(`${API}/recipe-analysis/freeze-point`, {
            recipe: {
              target_brix: parseFloat(recipe.target_brix) || 0,
              alcohol_flag: !!recipe.alcohol_flag,
              alcohol_percentage: recipe.alcohol_percentage,
              recommended_freeze_celsius: null,
            },
          });
          rfcToStore = freshResp.data?.computed_machine_temp_c ?? null;
        } catch (err) {
          console.warn('[FreezePoint] sync compute failed, fallback to live state', err);
          rfcToStore = computedAtRender ?? null;
        }
      }

      await axios.put(`${API}/recipes/${id}`, {
        session_id: sessionId,
        ...recipe,
        image_url: imageUrl,
        target_brix: parseFloat(recipe.target_brix),
        recommended_freeze_celsius: rfcToStore,
        ingredients: normalizedIngredients
      });
      toast.success(t('messages.success.updated'));
      navigate(`/recipes/${id}`);
    } catch (error) {
      console.error('Error updating recipe:', error);
      toast.error(t('addRecipe.updateError', 'Kunne ikke opdatere opskrift'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!recipe) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 fade-in" data-testid="edit-recipe-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold"
      >
        <FaArrowLeft /> {t('common.back')}
      </button>

      <div>
        <h1 className="text-4xl font-bold mb-2">{t('addRecipe.editTitle')}</h1>
        <p className="text-gray-600">{recipe.name}</p>
      </div>

      <form onSubmit={submitRecipe} className="space-y-6">
        {/* Image */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
          <h2 className="text-xl font-bold">Billede</h2>
          
          <div className="space-y-3">
            {imagePreview && (
              <div className="relative">
                <img 
                  src={imagePreview} 
                  alt="Preview" 
                  className="w-full h-64 object-cover rounded-lg"
                />
                {imageFile && (
                  <button
                    type="button"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(recipe.image_url);
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                  >
                    <FaTrash size={12} />
                  </button>
                )}
              </div>
            )}
            <div>
              <Label>{t('addRecipe.changeImageLabel')}</Label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                id="recipe-image-upload-edit"
                className="hidden"
              />
              <label
                htmlFor="recipe-image-upload-edit"
                className="block w-full cursor-pointer mt-2"
              >
                <div className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all">
                  <FaPlus />
                  <span>{t('addRecipe.chooseImage')}</span>
                </div>
                {imageFile && (
                  <p className="text-xs text-gray-600 mt-1 text-center">
                    {t('addRecipe.selectedFile')}: {imageFile.name}
                  </p>
                )}
              </label>
              <p className="text-xs text-gray-500 mt-1">{t('addRecipe.imageLimit')}</p>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4">
          <h2 className="text-xl font-bold">Basis Information</h2>
          
          <div>
            <Label>Navn *</Label>
            <Input
              value={recipe.name}
              onChange={(e) => setRecipe({...recipe, name: e.target.value})}
              placeholder="fx Min Specielle Jordbær Slush"
            />
          </div>

          <div>
            <Label>Beskrivelse *</Label>
            <Textarea
              value={recipe.description}
              onChange={(e) => setRecipe({...recipe, description: e.target.value})}
              placeholder="Beskriv din opskrift..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Type/Gruppe *</Label>
              <select
                value={recipe.type || 'klassisk'}
                onChange={(e) => {
                  // Edit-page rule (per user, Feb 2026): as long as the user
                  // has NOT typed in the Sukkergrad field themselves, every
                  // type change keeps syncing target_brix to the new type's
                  // default. This lets the admin bounce between types on a
                  // legacy brix=0 recipe (kaffe→cocktail→sukkerfri→…) and
                  // always see the right default. Once the user types a
                  // custom value, `brixManuallyEdited` flips true and
                  // future type-switches leave target_brix alone.
                  const newType = e.target.value;
                  const defaultBrix = TYPE_DEFAULT_BRIX[newType];
                  if (!brixManuallyEdited && defaultBrix !== undefined) {
                    setRecipe({ ...recipe, type: newType, target_brix: defaultBrix });
                  } else {
                    setRecipe({ ...recipe, type: newType });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-md"
                data-testid="edit-recipe-type-select"
              >
                <option value="klassisk">Klassisk</option>
                <option value="juice">Juice</option>
                <option value="smoothie">Smoothie</option>
                <option value="sodavand">Sodavand</option>
                <option value="cocktail">Cocktail</option>
                <option value="kaffe">Kaffe</option>
                <option value="sport">Sport</option>
                <option value="sukkerfri">Sukkerfri</option>
                <option value="maelk">Mælk</option>
              </select>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                Sukkergrad (°Bx)
                <button
                  type="button"
                  onClick={() => setShowBrixInfo(!showBrixInfo)}
                  className="text-cyan-600 hover:text-cyan-700 transition-colors"
                  title="Klik for mere information"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </button>
              </Label>
              <Input
                type="number"
                step="0.1"
                value={recipe.target_brix}
                onChange={(e) => {
                  setRecipe({...recipe, target_brix: e.target.value});
                  // Flip the manual flag so subsequent type-changes leave
                  // this custom value alone.
                  setBrixManuallyEdited(true);
                }}
                placeholder="13-15"
                data-testid="edit-recipe-target-brix"
              />
              {/* Ideal-band hint — same widget as AddRecipePage. Kept in
                  sync with server.py::admin_brix_outliers::BANDS so the
                  hint and outlier-report always agree. */}
              {TYPE_BRIX_BAND[recipe.type] && (() => {
                const [bmin, bmax] = TYPE_BRIX_BAND[recipe.type];
                const brixNum = parseFloat(recipe.target_brix);
                const outOfBand = !isNaN(brixNum) && (brixNum < bmin || brixNum > bmax);
                return (
                  <div
                    className={`text-xs mt-1 flex items-center gap-1 ${outOfBand ? 'text-amber-700' : 'text-gray-500'}`}
                    data-testid="edit-recipe-brix-band-hint"
                  >
                    <span aria-hidden="true">🎯</span>
                    <span>
                      Ideelt interval:{' '}
                      <strong className="tabular-nums">{bmin}–{bmax}°Bx</strong>
                      {outOfBand && (
                        <span className="ml-1">— udenfor typens normale område</span>
                      )}
                    </span>
                  </div>
                );
              })()}
              {showBrixInfo && (
                <div className="text-sm mt-2 bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500 p-3 rounded-r animate-in fade-in slide-in-from-top-2 duration-200">
                  <p className="font-semibold text-cyan-900 mb-1 flex items-center gap-2">
                    <span className="text-lg">ℹ️</span>
                    <span>Sukkerindhold</span>
                  </p>
                  <p className="text-gray-700 text-xs leading-relaxed">
                    Brix måler sukkerindholdet i din opskrift.<br/>
                    <span className="text-gray-600">Anbefalet: 13-15°Bx for god konsistens.</span><br/>
                    <a href="/brix-info" className="text-cyan-600 underline mt-1 inline-block" target="_blank">Læs mere om Brix →</a>
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label className="flex items-center gap-2">
                Basis Volumen (ml) <span className="text-red-500">*</span>
                <button
                  type="button"
                  onClick={() => setShowVolumeInfo(!showVolumeInfo)}
                  className="text-cyan-600 hover:text-cyan-700 transition-colors"
                  title="Klik for mere information"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </button>
              </Label>
              <Input
                type="number"
                step="100"
                required
                value={recipe.base_volume_ml || 2700}
                onChange={(e) => setRecipe({...recipe, base_volume_ml: parseInt(e.target.value)})}
                placeholder="2700"
              />
              {showVolumeInfo && (
                <div className="text-sm mt-2 bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500 p-3 rounded-r animate-in fade-in slide-in-from-top-2 duration-200">
                  <p className="font-semibold text-cyan-900 mb-1 flex items-center gap-2">
                    <span className="text-lg">ℹ️</span>
                    <span>{t('addRecipe.requiredField')}</span>
                  </p>
                  <p className="text-gray-700 text-xs leading-relaxed">
                    {t('addRecipe.volumeDescription')}<br/>
                    <span className="text-gray-600">{t('addRecipe.volumeExample')}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Anbefalet maskintemperatur (°C) — auto-beregnes af den
                fælles motor via /api/recipe-analysis/freeze-point.
                Redaktør/admin ser både beregnet + manuel + delta. */}
            <div>
              <Label className="flex items-center gap-2">
                Anbefalet maskintemperatur (°C)
                <span className="text-gray-400 text-xs font-normal">
                  (beregnes automatisk)
                </span>
              </Label>
              {(() => {
                const stored = recipe.recommended_freeze_celsius;
                const storedIsSet = stored !== null && stored !== undefined && stored !== '';
                const computed = freezeCalc?.computed_machine_temp_c;
                // Bestem tilstand:
                //  1. Brugerens eksplicitte valg (checkbox-klik) vinder.
                //  2. Fald tilbage til tolerance-heuristikken ved genindlæsning.
                const heuristicIsManual = storedIsSet && (
                  computed == null || Math.abs(parseFloat(stored) - computed) > 0.1
                );
                const isManual = freezeManualOverride ?? heuristicIsManual;
                const displayVal = isManual ? stored : (computed ?? '');
                return (
                  <>
                    <Input
                      type="number"
                      step="0.1"
                      value={displayVal}
                      disabled={!isManual}
                      onChange={(e) =>
                        setRecipe({ ...recipe, recommended_freeze_celsius: e.target.value })
                      }
                      placeholder={computed != null ? `${computed} (beregnet)` : 'Beregnes automatisk...'}
                      data-testid="edit-recipe-freeze-celsius"
                    />
                    <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!isManual}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Sæt "Brug beregnet" til — nulstil manuel værdi
                            setFreezeManualOverride(false);
                            setRecipe({ ...recipe, recommended_freeze_celsius: '' });
                          } else {
                            // Skift til manuel — pre-udfyld med beregnet, men
                            // marker eksplicit at brugeren nu er i manuel mode.
                            setFreezeManualOverride(true);
                            setRecipe({
                              ...recipe,
                              recommended_freeze_celsius: String(computed ?? ''),
                            });
                          }
                        }}
                        data-testid="edit-recipe-freeze-use-computed"
                      />
                      <span>Beregn maskintemperaturen automatisk</span>
                    </label>
                    {/* Admin/editor QA: vis både manuel og beregnet så
                        de kan vurdere om manuel værdi skal opdateres. */}
                    {(user?.role === 'admin' || user?.role === 'editor') && isManual && computed != null && (
                      <div
                        className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-700 flex flex-wrap gap-x-3"
                        data-testid="edit-recipe-freeze-diff"
                      >
                        <span>Manuelt sat: <strong>{stored}°C</strong></span>
                        <span>Beregnet: <strong>{computed}°C</strong></span>
                        <span>Diff: <strong>{(parseFloat(stored) - computed).toFixed(1)}°C</strong></span>
                        {freezeCalc?.physical_fp_c != null && (
                          <span>Fysisk FP: <strong>{freezeCalc.physical_fp_c}°C</strong></span>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Når funktionen er aktiveret, beregner SlushBook automatisk den anbefalede
                      maskintemperatur ud fra Brix og alkoholprocent. Slå funktionen fra for at
                      angive en fast temperatur manuelt.
                    </p>
                  </>
                );
              })()}
            </div>

            {/* Machine Analysis — differentiated by role.
                Redaktion (admin/editor) ser ALTID den fulde 48-maskine
                analyse (uafhængigt af deres egen maskine) — det er et
                kvalitetssikringsværktøj, ikke personlig brug. Almindelige
                brugere ser en kompakt personlig analyse mod deres egen
                maskine. Begge bygger på den fælles motor i
                services/recipe_analysis.py. */}
            {recipe.target_brix && recipe.base_volume_ml && (
              <div className="col-span-2">
                {(user?.role === 'admin' || user?.role === 'editor') ? (
                  <MachineValidationAlert recipe={recipe} selectedMachine={null} />
                ) : (
                  <PersonalRecipeAnalysis recipe={recipe} userMachineName={user?.machine_name || null} />
                )}
              </div>
            )}

            <div className="col-span-2">
              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={recipe.alcohol_flag}
                  onChange={(e) => setRecipe({...recipe, alcohol_flag: e.target.checked})}
                  className="w-4 h-4"
                />
                {t('addRecipe.containsAlcohol')}
              </Label>
            </div>
          </div>

          <div>
            <Label>Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Tilføj tag..."
              />
              <Button type="button" onClick={addTag}>
                Tilføj
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {recipe.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm flex items-center gap-2"
                >
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)}>
                    <FaTrash size={10} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <AliasesEditor
            value={recipe.match_aliases || []}
            onChange={(next) => setRecipe({ ...recipe, match_aliases: next })}
          />

          {/* Familie — finer-grained grouping inside Type. Optional. */}
          <div>
            <Label htmlFor="recipe-family">
              Familie
              <span className="ml-2 text-xs font-normal text-gray-500">(valgfri)</span>
            </Label>
            <Input
              id="recipe-family"
              data-testid="recipe-family-input"
              value={recipe.family || ''}
              onChange={(e) => setRecipe({ ...recipe, family: e.target.value })}
              placeholder="fx Margarita, Piña Colada, Frappé, Protein"
            />
            <p className="text-xs text-gray-500 mt-1">
              Bruges til at gruppere opskrifter inden for samme Type. Lad være tom hvis ikke relevant.
            </p>
          </div>
        </div>

        {/* Ingredients */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4 ingredients-section">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Ingredienser</h2>
            <Button type="button" onClick={addIngredient} size="sm">
              <FaPlus className="mr-2" /> Tilføj
            </Button>
          </div>

          {recipe.ingredients.map((ingredient, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3 ingredient-item">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Ingrediens {index + 1}</span>
                <div className="flex items-center gap-2">
                  {/* Move up button */}
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => moveIngredientUp(index)}
                      className="text-cyan-500 hover:text-cyan-700 p-1"
                      title="Flyt op"
                    >
                      <FaArrowUp size={14} />
                    </button>
                  )}
                  {/* Move down button */}
                  {index < recipe.ingredients.length - 1 && (
                    <button
                      type="button"
                      onClick={() => moveIngredientDown(index)}
                      className="text-cyan-500 hover:text-cyan-700 p-1"
                      title="Flyt ned"
                    >
                      <FaArrowDown size={14} />
                    </button>
                  )}
                  {/* Delete button */}
                  {recipe.ingredients.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeIngredient(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Navn *"
                  value={ingredient.name}
                  onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                />
                <Input
                  placeholder="Kategori *"
                  value={ingredient.category_key}
                  onChange={(e) => updateIngredient(index, 'category_key', e.target.value)}
                />
                <Input
                  type="number"
                  placeholder="Mængde"
                  value={ingredient.quantity}
                  onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
                <select
                  value={ingredient.unit}
                  onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-md"
                >
                  {supportedUnits.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
                <select
                  value={ingredient.role}
                  onChange={(e) => updateIngredient(index, 'role', e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-md"
                >
                  <option value="required">Nødvendig</option>
                  <option value="optional">Valgfri</option>
                </select>
                <Input
                  type="number"
                  placeholder="Brix (valgfrit)"
                  value={ingredient.brix || ''}
                  onChange={(e) => updateIngredient(index, 'brix', e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Steps */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-4 steps-section">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Fremgangsmåde</h2>
            <Button type="button" onClick={addStep} size="sm">
              <FaPlus className="mr-2" /> Tilføj Trin
            </Button>
          </div>

          {recipe.steps.map((step, index) => (
            <div key={index} className="flex gap-3">
              <span className="flex-shrink-0 w-8 h-8 bg-cyan-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                {index + 1}
              </span>
              <Textarea
                value={step}
                onChange={(e) => updateStep(index, e.target.value)}
                placeholder={`Trin ${index + 1}...`}
                rows={2}
                className="flex-1"
              />
              <div className="flex flex-col gap-1">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => moveStepUp(index)}
                    className="text-cyan-500 hover:text-cyan-700 p-1"
                    title="Flyt op"
                  >
                    <FaArrowUp size={14} />
                  </button>
                )}
                {index < recipe.steps.length - 1 && (
                  <button
                    type="button"
                    onClick={() => moveStepDown(index)}
                    className="text-cyan-500 hover:text-cyan-700 p-1"
                    title="Flyt ned"
                  >
                    <FaArrowDown size={14} />
                  </button>
                )}
              </div>
              {recipe.steps.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  className="text-red-500 hover:text-red-700 p-2"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Publish Toggle */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                {recipe.is_published ? '🌍 Offentlig opskrift' : '🔒 Privat opskrift'}
              </h3>
              <p className="text-sm text-gray-600">
                {recipe.is_published 
                  ? 'Denne opskrift er synlig for alle brugere' 
                  : 'Kun du kan se denne opskrift'}
              </p>
              {/* Show info if approved and user is not admin */}
              {recipe.status === 'approved' && !isAdmin() && (
                <p className="text-xs text-blue-600 mt-1">
                  ℹ️ Denne opskrift er godkendt. Ændringer kræver ny godkendelse.
                </p>
              )}
              {/* Show info if approved and user is not admin */}
              {recipe.status === 'approved' && !isAdmin() && (
                <p className="text-xs text-blue-600 mt-1">
                  ℹ️ Denne opskrift er godkendt. Ændringer kræver ny godkendelse.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                // Everyone (including admin) can toggle their own recipes
                const newPublishedState = !recipe.is_published;
                setRecipe({...recipe, is_published: newPublishedState});
                // Reset confirmation when toggling off
                if (!newPublishedState) {
                  setImageRightsConfirmed(false);
                }
              }}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                recipe.is_published ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  recipe.is_published ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          
          {/* Copyright Confirmation ONLY if image is uploaded */}
          {recipe.is_published && imageFile && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-amber-800 mb-2">
                  ⚠️ <strong>Vigtigt om billede:</strong> Du har uploadet et billede til denne opskrift.
                </p>
                <p className="text-xs text-amber-700">
                  Sørg for at du har rettighederne til billedet før du deler opskriften offentligt.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={imageRightsConfirmed}
                  onChange={(e) => setImageRightsConfirmed(e.target.checked)}
                  className="mt-1 w-4 h-4 text-cyan-600 rounded focus:ring-cyan-500"
                />
                <span className="text-sm text-gray-700">
                  Jeg bekræfter at jeg har rettighederne til det uploadede billede. 
                  <span className="text-red-600">*</span>
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Annullér
          </Button>
          <Button
            type="submit"
            disabled={saving || (recipe.is_published && imageFile && !imageRightsConfirmed)}
            className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
          >
            {saving ? t('addRecipe.saving') : t('addRecipe.saveChanges', 'Gem Ændringer')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditRecipePage;
